import { PlusOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { makeAutoObservable } from "mobx";
import * as React from "react";
import {
  extensionEventLabels,
  getBindingsForEvent,
  hasBindingForEvent,
  removeBindingForEvent,
  setBindingForEvent,
  ZBoundExtension,
  ZExtensionEventType,
  ZExtensionFormBinding,
} from "src/business/extensionBindings";
import { extensionToBound } from "src/business/extensionBindings/mapExtensions";
import {
  ZExtension,
  ZExtensionScope,
} from "src/business/extensions/ZExtension";
import { MdmTable } from "src/components/MdmTable";
import { RtStore } from "src/components/ReactiveTable/RtStore";
import { ToolBox2 } from "src/components/ToolBox2";

import * as styles from "./EventsAndExtensions.module.less";
import { createExtensionBindingTableColumns } from "./extensionBindingColumns";
import { ExtensionSelectModal } from "./ExtensionSelectModal";

type PropsEventExtensionSection = {
  event: ZExtensionEventType;
  scope: ZExtensionScope;
  bindings: ZExtensionFormBinding[];
  onChange?: (bindings: ZExtensionFormBinding[]) => void;
  disabled?: boolean;
};

export const EventExtensionSection: React.FC<PropsEventExtensionSection> = ({
  event,
  scope,
  bindings,
  onChange,
  disabled,
}) => {
  const [openSelectModal, setOpenSelectModal] = React.useState(false);
  const [sectionStore] = React.useState(createEventExtensionSectionStore);

  React.useEffect(() => {
    sectionStore.setRows(getBindingsForEvent(bindings, event));
  }, [bindings, event, sectionStore]);

  const columns = createExtensionBindingTableColumns(
    () => onChange?.(removeBindingForEvent(bindings, event)),
    false,
  );

  const onSelect = (extension: ZExtension) => {
    onChange?.(
      setBindingForEvent(bindings, event, extensionToBound(extension)),
    );
    setOpenSelectModal(false);
  };

  return (
    <ToolBox2
      mainTool={
        <div className={styles.sectionTitle}>{extensionEventLabels[event]}</div>
      }
      extraTools={
        <Button
          icon={<PlusOutlined />}
          onClick={() => setOpenSelectModal(true)}
          disabled={disabled || hasBindingForEvent(bindings, event)}
        >
          Добавить
        </Button>
      }
    >
      <MdmTable
        store={sectionStore.tableStore}
        columns={columns}
        resizableColumns
        verticalMode="normal"
        components={{ Footer: () => null }}
      />
      {openSelectModal && (
        <ExtensionSelectModal
          scope={scope}
          close={() => setOpenSelectModal(false)}
          onSelect={onSelect}
        />
      )}
    </ToolBox2>
  );
};

const createEventExtensionSectionStore = () => {
  let rows: ZBoundExtension[] = [];
  const tableStore = new RtStore<ZBoundExtension, {}>({
    rowKey: "id",
    loader: async () => ({
      rows,
      totalRows: rows.length,
    }),
  });

  return makeAutoObservable(
    {
      tableStore,
      setRows(newRows: ZBoundExtension[]) {
        rows = newRows;
        tableStore.onDataChanged();
      },
    },
    { tableStore: false },
  );
};
